import { ChangeDetectionStrategy, Component, input } from '@angular/core';
import { NgIcon } from '@ng-icons/core';

/**
 * A single icon glyph.
 *
 * Thin wrapper over ng-icons' own <ng-icon>, adding nothing behavioural
 * — its only job is a consistent default size and clean inline layout,
 * so every icon in the app shares one baseline instead of each call
 * site reinventing it.
 *
 * Icons are NOT pre-registered here. ng-icons is tree-shakeable by
 * design — only the icons a component actually imports and hands to
 * `provideIcons({...})` reach the bundle — and pre-registering a fixed
 * set on hh-icon itself would defeat that for every consumer, whether
 * they use those icons or not. Each feature component registers the
 * specific icons it needs, in its own `providers`:
 *
 *   import { phosphorGearSixBold } from '@ng-icons/phosphor-icons/bold';
 *   import { provideIcons } from '@ng-icons/core';
 *
 *   @Component({
 *     providers: [provideIcons({ phosphorGearSixBold })],
 *     ...
 *   })
 *
 *   <hh-icon name="phosphorGearSixBold" />
 *
 * The brand's chosen set is Phosphor, Bold weight
 * (`@ng-icons/phosphor-icons/bold`) — see the design system's own
 * iconography guideline ("rounded, confident stroke that matches the
 * chunky brand shapes"). Nothing stops a caller registering icons from
 * a different set; hh-icon only ever forwards whatever `name` it is
 * given.
 *
 * Size defaults to 1em, so an icon dropped into a button label or a
 * heading scales with the surrounding text automatically. Colour is
 * unset by default for the same reason: ng-icons' SVGs use
 * fill="currentColor", so the icon already inherits CSS `color` — set
 * `color` only when an icon needs to differ from its surrounding text
 * (a status glyph, say), not for the common case.
 */
@Component({
  selector: 'hh-icon',
  standalone: true,
  imports: [NgIcon],
  template: `
    <ng-icon
      [name]="name()"
      [size]="size()"
      [color]="color()"
      [strokeWidth]="strokeWidth()"
    />
  `,
  styleUrl: './hh-icon.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HhIconComponent {
  readonly name = input('');

  /** Any valid CSS size (e.g. '1em', '1.5rem', '24px'). Defaults to
   * '1em' so the icon tracks the surrounding font size. */
  readonly size = input('1em');

  /** Unset inherits currentColor from surrounding CSS — see class doc. */
  readonly color = input<string | undefined>(undefined);

  /** Only meaningful for stroke-based icon sets; Phosphor's Bold
   * weight is filled, not stroked, so this has no effect there. */
  readonly strokeWidth = input<string | number | undefined>(undefined);
}
